import React, { useState, useRef } from 'react';
import JSZip from 'jszip';

const BatchResizer = () => {
  const [images, setImages] = useState([]);
  const [resizeSettings, setResizeSettings] = useState({
    width: 800,
    height: 600,
    maintainAspectRatio: true,
    quality: 0.9,
    format: 'JPEG'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedImages, setProcessedImages] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    const newImages = imageFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      status: 'pending'
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (id) => {
    setImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      // Clean up object URLs
      const removed = prev.find(img => img.id === id);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const resizeImage = (file, settings) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = settings;
        
        if (settings.maintainAspectRatio) {
          const aspectRatio = img.width / img.height;
          if (width / height > aspectRatio) {
            width = height * aspectRatio;
          } else {
            height = width / aspectRatio;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Use better scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve({
              blob,
              width,
              height,
              originalWidth: img.width,
              originalHeight: img.height
            });
          },
          `image/${settings.format.toLowerCase()}`,
          settings.quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const processImages = async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    setProcessedImages([]);

    const results = [];

    for (const image of images) {
      try {
        const result = await resizeImage(image.file, resizeSettings);
        const processedImage = {
          ...image,
          ...result,
          status: 'completed',
          downloadUrl: URL.createObjectURL(result.blob),
          newSize: result.blob.size,
          newName: `resized_${image.name.replace(/\.[^/.]+$/, "")}.${resizeSettings.format.toLowerCase()}`
        };
        results.push(processedImage);
        setProcessedImages(prev => [...prev, processedImage]);
      } catch (error) {
        console.error('Error processing image:', error);
        results.push({
          ...image,
          status: 'error',
          error: error.message
        });
      }
    }

    setIsProcessing(false);
  };

  const downloadImage = (image) => {
    const link = document.createElement('a');
    link.href = image.downloadUrl;
    link.download = image.newName;
    link.click();
  };

  const downloadAll = async () => {
    if (processedImages.length === 0) return;
    
    setIsDownloading(true);
    const zip = new JSZip();
    const completedImages = processedImages.filter(image => image.status === 'completed');
    
    if (completedImages.length === 0) {
      setIsDownloading(false);
      return;
    }
    
    // Add each image to the zip
    for (const image of completedImages) {
      try {
        // Convert blob URL to blob
        const response = await fetch(image.downloadUrl);
        const blob = await response.blob();
        
        // Get file extension based on format
        const extension = resizeSettings.format.toLowerCase() === 'jpeg' ? 'jpg' : resizeSettings.format.toLowerCase();
        const fileName = `${image.name.split('.')[0]}_${Math.round(image.width)}x${Math.round(image.height)}.${extension}`;
        
        zip.file(fileName, blob);
      } catch (error) {
        console.error('Error adding image to zip:', error);
      }
    }
    
    try {
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resized_images_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error creating zip file:', error);
      // Fallback to individual downloads
      processedImages.forEach(image => {
        if (image.status === 'completed') {
          setTimeout(() => downloadImage(image), 100);
        }
      });
    }
    
    setIsDownloading(false);
  };

  const clearAll = () => {
    // Clean up object URLs
    images.forEach(img => URL.revokeObjectURL(img.preview));
    processedImages.forEach(img => {
      if (img.downloadUrl) URL.revokeObjectURL(img.downloadUrl);
    });
    
    setImages([]);
    setProcessedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-4">Batch Image Resizer</h1>
          <p className="text-gray-300 mb-6">
            Resize multiple images at once with custom dimensions. All processing happens locally in your browser for maximum privacy and speed.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
              <h2 className="text-xl font-semibold text-white mb-4">Resize Settings</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Width (px)
                  </label>
                  <input
                    type="number"
                    value={resizeSettings.width}
                    onChange={(e) => setResizeSettings(prev => ({ ...prev, width: parseInt(e.target.value) || 800 }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="5000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Height (px)
                  </label>
                  <input
                    type="number"
                    value={resizeSettings.height}
                    onChange={(e) => setResizeSettings(prev => ({ ...prev, height: parseInt(e.target.value) || 600 }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="5000"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="maintain-aspect"
                    checked={resizeSettings.maintainAspectRatio}
                    onChange={(e) => setResizeSettings(prev => ({ ...prev, maintainAspectRatio: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="maintain-aspect" className="ml-2 text-sm text-gray-300">
                    Maintain aspect ratio
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quality ({Math.round(resizeSettings.quality * 100)}%)
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={resizeSettings.quality}
                    onChange={(e) => setResizeSettings(prev => ({ ...prev, quality: parseFloat(e.target.value) }))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Output Format
                  </label>
                  <select
                    value={resizeSettings.format}
                    onChange={(e) => setResizeSettings(prev => ({ ...prev, format: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="JPEG">JPEG</option>
                    <option value="PNG">PNG</option>
                    <option value="WEBP">WEBP</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Upload Section */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-4">Upload Images</h2>
              
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-gray-500 transition-colors">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-300 mb-4">Drag and drop images here, or click to select</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg cursor-pointer transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Select Images
                </label>
              </div>

              {images.length > 0 && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={processImages}
                    disabled={isProcessing}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {isProcessing ? 'Processing...' : `Resize ${images.length} Images`}
                  </button>
                  <button
                    onClick={clearAll}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Images Grid */}
          <div className="lg:col-span-2">
            {images.length > 0 && (
              <div className="bg-gray-800 rounded-lg border border-gray-700 mb-6">
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">
                    Images ({images.length})
                  </h2>
                  {processedImages.length > 0 && (
                    <button
                      onClick={downloadAll}
                      disabled={isDownloading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                      {isDownloading ? (
                        <>
                          <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Creating ZIP...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m0 0l3-3m-3 3h7.586l-3-3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Download as ZIP
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                <div className="p-4 max-h-96 overflow-y-auto">
                  <div className="space-y-4">
                    {images.map((image) => {
                      const processed = processedImages.find(p => p.id === image.id);
                      return (
                        <div key={image.id} className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
                          <img
                            src={image.preview}
                            alt={image.name}
                            className="w-16 h-16 object-cover rounded border border-gray-600"
                          />
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{image.name}</p>
                            <p className="text-gray-400 text-sm">{formatFileSize(image.size)}</p>
                            {processed && (
                              <p className="text-green-400 text-sm">
                                Resized: {processed.width}×{processed.height} ({formatFileSize(processed.newSize)})
                              </p>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            {processed?.status === 'completed' && (
                              <button
                                onClick={() => downloadImage(processed)}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                              >
                                Download
                              </button>
                            )}
                            
                            <button
                              onClick={() => removeImage(image.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            {images.length === 0 && (
              <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-semibold text-white mb-2">No Images Selected</h3>
                <p className="text-gray-400">Upload multiple images to resize them all at once with your custom settings.</p>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Lightning Fast</h3>
            <p className="text-gray-400 text-sm">
              Process multiple images simultaneously with optimized algorithms for maximum speed.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">100% Private</h3>
            <p className="text-gray-400 text-sm">
              All processing happens locally in your browser. Your images never leave your device.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m0 0l3-3m-3 3h7.586l-3-3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">ZIP Downloads</h3>
            <p className="text-gray-400 text-sm">
              Download all your resized images in a single convenient ZIP file with organized filenames.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchResizer; 